/**
 * Single portal skill-level vocabulary (epic #3232 / issue #3241).
 *
 * Canonical order: guided < independent < experienced < can_teach.
 * "wants to learn" is a flag, never a level. Legacy wire values
 * (beginner / intermediate / expert / interest) are accepted so older
 * payloads and service-skill minima still match.
 */

export const CANONICAL_LEVELS = ['guided', 'independent', 'experienced', 'can_teach'] as const;
export type CanonicalLevel = (typeof CANONICAL_LEVELS)[number];

export const LEGACY_LEVELS = ['beginner', 'intermediate', 'expert', 'interest'] as const;
export type LegacyLevel = (typeof LEGACY_LEVELS)[number];

export const SKILL_CATEGORY_KEYS = [
  'service',
  'diagnostics',
  'electrical_hv',
  'hvac',
  'chassis_brakes',
  'body_glass',
  'brand',
  'equipment_operation',
  'inspection',
  'specialty',
  'other',
] as const;
export type SkillCategoryKey = (typeof SKILL_CATEGORY_KEYS)[number];

export const CERTIFICATE_STATUSES = ['none', 'valid', 'expiring', 'expired'] as const;
export type CertificateStatus = (typeof CERTIFICATE_STATUSES)[number];

export type CertificateBadge = 'valid' | 'expiring' | 'expired' | 'missing' | 'none';

const LEGACY_TO_CANONICAL: Record<string, CanonicalLevel | null> = {
  beginner: 'guided',
  intermediate: 'independent',
  expert: 'experienced',
  interest: null,
  guided: 'guided',
  independent: 'independent',
  experienced: 'experienced',
  can_teach: 'can_teach',
};

const CANONICAL_TO_LEGACY: Record<CanonicalLevel, Exclude<LegacyLevel, 'interest'>> = {
  guided: 'beginner',
  independent: 'intermediate',
  experienced: 'expert',
  can_teach: 'expert',
};

/** Module-scope level metadata — resolve `labelKey` with `t()` in components. */
export const SKILL_LEVEL_OPTIONS: ReadonlyArray<{
  value: CanonicalLevel;
  rank: number;
  labelKey: string;
}> = [
  { value: 'guided', rank: 1, labelKey: 'skills.levels.guided' },
  { value: 'independent', rank: 2, labelKey: 'skills.levels.independent' },
  { value: 'experienced', rank: 3, labelKey: 'skills.levels.experienced' },
  { value: 'can_teach', rank: 4, labelKey: 'skills.levels.canTeach' },
];

export const SKILL_CATEGORY_OPTIONS: ReadonlyArray<{
  value: SkillCategoryKey;
  labelKey: string;
}> = SKILL_CATEGORY_KEYS.map((value) => ({
  value,
  labelKey: `skills.categories.${value}`,
}));

export function isCanonicalLevel(value: string): value is CanonicalLevel {
  return (CANONICAL_LEVELS as readonly string[]).includes(value);
}

export function isSkillCategoryKey(value: string): value is SkillCategoryKey {
  return (SKILL_CATEGORY_KEYS as readonly string[]).includes(value);
}

/**
 * Accept both vocabularies. `interest` and unknown/empty values yield null
 * (no level — a wants-to-learn row, not a rank).
 */
export function fromAnyLevel(value: string | null | undefined): CanonicalLevel | null {
  if (value === null || value === undefined || value === '') return null;
  const mapped = LEGACY_TO_CANONICAL[value];
  return mapped === undefined ? null : mapped;
}

export function toLegacyLevel(level: CanonicalLevel | string | null | undefined): LegacyLevel {
  const canonical = typeof level === 'string' ? fromAnyLevel(level) : level ?? null;
  if (canonical === null) return 'interest';
  return CANONICAL_TO_LEGACY[canonical];
}

/** 1–4 for a held level; 0 when there is no rank (wants-only / unknown). */
export function canonicalLevelRank(level: CanonicalLevel | string | null | undefined): number {
  const canonical = typeof level === 'string' ? fromAnyLevel(level) : level ?? null;
  if (canonical === null) return 0;
  return CANONICAL_LEVELS.indexOf(canonical) + 1;
}

/**
 * True when `held` meets or exceeds `minimum`. Both sides accept either
 * vocabulary. A wants-only / unknown held level never qualifies.
 */
export function meetsMinimum(
  held: string | null | undefined,
  minimum: string | null | undefined,
): boolean {
  const heldRank = canonicalLevelRank(held);
  if (heldRank <= 0) return false;
  const minCanonical = fromAnyLevel(minimum) ?? 'guided';
  return heldRank >= CANONICAL_LEVELS.indexOf(minCanonical) + 1;
}

export function categoryLabelKey(category: string | null | undefined): string {
  if (category && isSkillCategoryKey(category)) {
    return `skills.categories.${category}`;
  }
  return 'skills.categories.other';
}

export function levelLabelKey(level: CanonicalLevel | string | null | undefined): string {
  const canonical = fromAnyLevel(level ?? null);
  if (!canonical) return 'skills.levels.none';
  const option = SKILL_LEVEL_OPTIONS.find((entry) => entry.value === canonical);
  return option?.labelKey ?? 'skills.levels.none';
}

/**
 * Visual certificate state for a matrix cell. "missing" is shown only when
 * the skill requires a valid certificate and none is on file.
 */
export function certificateBadge(args: {
  status: CertificateStatus | undefined;
  requiresValidCertificate: boolean;
}): CertificateBadge {
  if (args.status === 'valid') return 'valid';
  if (args.status === 'expiring') return 'expiring';
  if (args.status === 'expired') return 'expired';
  if (args.requiresValidCertificate) return 'missing';
  return 'none';
}

export function certificateBadgeLabelKey(badge: CertificateBadge): string {
  switch (badge) {
    case 'valid':
      return 'skills.certificate.valid';
    case 'expiring':
      return 'skills.certificate.expiring';
    case 'expired':
      return 'skills.certificate.expired';
    case 'missing':
      return 'skills.certificate.missing';
    default:
      return 'skills.certificate.none';
  }
}

/** Map a catalog category onto the legacy `type` still required on write. */
export function legacyTypeForCategory(category: SkillCategoryKey): 'service_category' | 'brand_certification' {
  return category === 'brand' ? 'brand_certification' : 'service_category';
}

export function categoryFromLegacyType(type: string): SkillCategoryKey {
  if (type === 'brand_certification') return 'brand';
  if (type === 'equipment') return 'equipment_operation';
  return 'service';
}
